import { useEffect, useState } from 'react'
import { track } from '../../lib/tracking'
import { camelToKebab } from '../../lib/utils'
import CustomInput from './CustomInput'
import { validateFields } from './validation'
import { MortgageTerms } from '../../api/httpHandlers'
import { AppAction } from '../../appState'
import { Caption } from '@sbab-ui/components'

interface Props {
  mortgageTerms: MortgageTerms
  interestRateAppDispatch: React.Dispatch<AppAction>
}

export interface InputFieldsState {
  estimatedValue: { value: number; error: string; hasBeenTouched: boolean }
  mortgage: { value: number; error: string; hasBeenTouched: boolean }
  wantedLoanAmount: { value: number; error: string; hasBeenTouched: boolean }
  fieldsAreValid: boolean
}

export type FieldName = 'estimatedValue' | 'mortgage' | 'wantedLoanAmount'

export default function DebouncedInputs({
  mortgageTerms,
  interestRateAppDispatch,
}: Readonly<Props>) {
  const [inputFields, setInputFields] = useState<InputFieldsState>({
    estimatedValue: { value: 0, error: '', hasBeenTouched: false },
    mortgage: { value: 0, error: '', hasBeenTouched: false },
    wantedLoanAmount: { value: 0, error: '', hasBeenTouched: false },
    fieldsAreValid: false,
  })
  const [rawEstimatedValue, setRawEstimatedValue] = useState<string>('')
  const [rawMortgage, setRawMortgage] = useState<string>('')
  const [rawWantedLoanAmount, setRawWantedLoanAmount] = useState<string>('')
  const { estimatedValue, mortgage, wantedLoanAmount, fieldsAreValid } = inputFields

  function valueChangeHandler(field: FieldName, value: string) {
    if (field === 'estimatedValue') {
      setRawEstimatedValue(value)
    } else if (field === 'mortgage') {
      setRawMortgage(value)
    } else if (field === 'wantedLoanAmount') {
      setRawWantedLoanAmount(value)
    }
    const newValue = +value.replace(/\D+/g, '')
    if (newValue === inputFields[field].value) return
    track('user-input', camelToKebab(field))
    const fieldToUpdate = inputFields[field]
    const updatedField = { ...fieldToUpdate, value: newValue, hasBeenTouched: true }
    const updatedInputFields = inputFields
    updatedInputFields[field] = updatedField
    const newInputFieldsState: InputFieldsState = validateFields(updatedInputFields, mortgageTerms)
    setInputFields(newInputFieldsState)
  }

  // Debounce user input
  useEffect(() => {
    if (
      !estimatedValue.hasBeenTouched &&
      !mortgage.hasBeenTouched &&
      !wantedLoanAmount.hasBeenTouched
    ) {
      return
    }
    interestRateAppDispatch({ type: 'statusChange', status: 'userIsTyping' })
    const delay = 500 // Debounce time in milliseconds
    const timeoutId = setTimeout(() => {
      if (fieldsAreValid) {
        interestRateAppDispatch({
          type: 'fieldsAreValid',
          fields: {
            estimatedValue: estimatedValue.value,
            mortgage: mortgage.value,
            wantedLoanAmount: wantedLoanAmount.value,
          },
        })
      } else {
        interestRateAppDispatch({ type: 'statusChange', status: 'formError' })
      }
    }, delay)

    return () => clearTimeout(timeoutId)
  }, [fieldsAreValid, interestRateAppDispatch, estimatedValue, mortgage, wantedLoanAmount])

  return (
    <>
      <CustomInput
        id="estimatedValue"
        value={rawEstimatedValue}
        valueChangeHandler={valueChangeHandler}
        suffix="kr"
        label="Vad tror du att din bostad är värd?"
        errorMessage={estimatedValue.error}
      />
      <CustomInput
        id="mortgage"
        value={rawMortgage}
        valueChangeHandler={valueChangeHandler}
        suffix="kr"
        label="Hur mycket har du i bolån på bostaden idag?"
        errorMessage={mortgage.error}
        tooltipAriaLabel="Klicka för att läsa mer om bolån"
      />
      <CustomInput
        id="wantedLoanAmount"
        value={rawWantedLoanAmount}
        valueChangeHandler={valueChangeHandler}
        suffix="kr"
        label="Hur mycket vill du låna till energirenoveringen?"
        errorMessage={wantedLoanAmount.error}
        tooltipAriaLabel="Klicka för att läsa mer om Kilowattlånet"
        tooltip={
          <Caption mb="4x">
            Fyll i hur mycket du vill låna. Om du väljer att skicka in en ansökan kan vi ändra
            beloppet senare efter dina önskemål.
          </Caption>
        }
      />
    </>
  )
}
